import { Flex, Image, View } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import Button from '@react/react-spectrum/Button';
import p from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { supportsNextGenImages } from '@exchange-frontends/utils';

// Styled Components

/**
 * Main banner container.
 * @param {string} [$imgUrl] - URL towards the image that will be used as the background. If not provided the background will be black **(optional, defaults to `$spectrumColor`)**.
 * @param {string} [$backgroundPosition] - The value of the background-position rule **(optional, default: `center`)**.
 * @param {string} [$spectrumColor] - Spectrum color value for the background (eg: `red-500`). Will be used if `$imgUrl` is not set **(optional, defaults to `$bgColor`)**.
 * @param {string} [$bgColor] - CSS color value for the background (eg: `blue`). Will be used if `$spectrumColor` is not set **(optional, default: black)**.
 */
const BannerContainer = styled.div`
  display: flex;
  position: relative;
  z-index: 0;
  overflow: hidden;
  width: 100%;
  min-height: 224px;
  padding: 0;
  justify-content: space-between;
  align-items: center;
  border-radius: var(--spectrum-global-dimension-size-50);
  background: ${({ $imgUrl, $spectrumColor, $bgColor }) =>
    $imgUrl
      ? 'none'
      : $spectrumColor
      ? `var(--spectrum-global-color-${$spectrumColor})`
      : $bgColor
      ? $bgColor
      : 'black'};
  background-size: cover;
  background-position: ${({ $backgroundPosition }) => $backgroundPosition ?? 'center'};
  box-sizing: border-box;
  font-size: var(--static-size);
  ${({ $otherStyles }) =>
    Object.keys($otherStyles)
      .map((key) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${$otherStyles[key]}`)
      .join(';')};
`;

/**
 * Banner content container element
 * @memberof LinkBanner
 * @type {styled-component}
 */
const BannerContentContainer = styled.div`
  background: ${({ $bgColor }) => $bgColor};
  border-radius: var(--spectrum-global-dimension-static-size-50);
  padding: ${({ $additionalImg, $reducePadding }) =>
    $additionalImg || $reducePadding
      ? 'var(--spectrum-global-dimension-size-400)'
      : 'var(--spectrum-global-dimension-static-size-600)'};
  width: 100%;
  height: 100%;
  display: flex;
  align-items: ${({ $alignItems }) => $alignItems ?? 'flex-start'};
  justify-content: center;
  flex-direction: column;
  box-sizing: border-box;
`;

/**
 * Title element.
 * @memberof LinkBanner
 * @type {styled-component}
 */
const Title = styled.h2`
  font-size: var(--spectrum-global-dimension-font-size-500);
  margin: 0;
  margin-bottom: var(--spectrum-global-dimension-size-100);
  color: var(--spectrum-global-color-gray-50);
  font-weight: bold;
  max-width: 525px;
`;

/**
 * Subtitle element, built upon the Title element.
 */
const Subtitle = styled.h3`
  margin: 0;
  color: var(--spectrum-global-color-gray-50);
  max-width: 525px;
  font-size: var(--spectrum-global-dimension-font-size-200);
  font-weight: medium;
  margin-bottom: 0;
`;

/**
 * Container for the logo image.
 * @memberof LinkBanner
 * @type {styled-component}
 */
const LogoContainer = styled.div`
  max-height: var(--spectrum-global-dimension-size-300);
  width: auto;
  margin-bottom: var(--spectrum-global-dimension-size-150);
`;

/**
 * Background image container for the LinkBanner
 * @memberof LinkBanner
 * @type {styled-component}
 */
const BackgroundImgContainer = styled.div`
  height: 100%;
  min-width: 100%;
  position: absolute;
  overflow: hidden;
  z-index: -1;
`;

/**
 * Function to transform the image URL from any extension to .webp if the browser supports .webp
 * @param {string} url - Image URL that you change to .webp
 */
const getNextGenImageUrl = (url) =>
  supportsNextGenImages() ? url?.split('.')?.slice(0, -1)?.join('.') + '.webp' : url;

/**
 * Banner with title, subtitle, button and optional image and logo.
 */
const LinkBanner = (props) => {
  const {
    bgColor,
    bgImgUrl,
    bgSpectrumColor,
    title,
    subtitle,
    buttonText,
    buttonHref,
    logoImgUrl,
    additionalImg,
    bgImgPosition,
    target,
    rel,
    useVanillaLink = false,
    reducePadding,
    ...rest
  } = props;
  const { matchedBreakpoints } = useBreakpoint();

  return (
    <BannerContainer
      $imgUrl={bgImgUrl}
      $bgColor={bgColor}
      $spectrumColor={bgSpectrumColor}
      $backgroundPosition={bgImgPosition}
      $additionalImg={additionalImg}
      $otherStyles={rest}
    >
      {bgImgUrl && (
        <BackgroundImgContainer>
          <object
            type="image/webp"
            style={{ height: '100%', width: '100%', objectFit: 'cover' }}
            data={getNextGenImageUrl(bgImgUrl)}
            role="presentation"
          >
            <img src={bgImgUrl} alt="" style={{ height: '100%', minWidth: '100%' }} />
          </object>
        </BackgroundImgContainer>
      )}
      <Flex
        direction={additionalImg && !matchedBreakpoints.includes('S') ? `column` : `row`}
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        <BannerContentContainer
          $bgColor={bgImgUrl ? 'rgba(0,0,0,0.6)' : 'none'}
          $additionalImg={additionalImg}
          $reducePadding={reducePadding}
        >
          {logoImgUrl && (
            <LogoContainer>
              <object
                type="image/webp"
                style={{ height: '100%', width: '100%' }}
                data={getNextGenImageUrl(logoImgUrl)}
                role="presentation"
              >
                <img src={logoImgUrl} alt="" style={{ height: '100%', width: '100%' }} />
              </object>
            </LogoContainer>
          )}
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
          <View marginTop="size-300">
            {useVanillaLink ? (
              <Button
                href={buttonHref}
                rel={rel}
                target={target}
                element="a"
                variant="overBackground"
                data-launchid={buttonText}
              >
                {buttonText}
              </Button>
            ) : (
              <Link
                id={buttonText}
                to={buttonHref}
                role="button"
                tabIndex="0"
                className="spectrum-Button spectrum-Button--overBackground"
              >
                <span className="spectrum-Button-label" data-launchid={buttonText}>
                  {buttonText}
                </span>
              </Link>
            )}
          </View>
        </BannerContentContainer>
        {additionalImg && matchedBreakpoints.includes('S') && (
          <div style={{ width: '45%', overflow: ' hidden' }}>
            <object
              type="image/webp"
              style={{ height: '100%', width: '100%', display: 'block' }}
              data={getNextGenImageUrl(additionalImg)}
            >
              <Image width="100%" src={additionalImg} alt="" />
            </object>
          </div>
        )}
      </Flex>
    </BannerContainer>
  );
};

LinkBanner.propTypes = {
  /** The URL of the additional image for the right side of the banner */
  additionalImg: p.string,
  /** The URL of the image that will be used in the background. If not set then `bgSpectrumColor` will be used. */
  bgImgUrl: p.string,
  /** Spectrum global color string for the background (eg: red-500). If not set then `bgColor` will be used. */
  bgSpectrumColor: p.string,
  /** CSS global color string for the background (eg: blue). If not set then black will be used. */
  bgColor: p.string,
  /** Title value. */
  title: p.string.isRequired,
  /** Subtitle value. */
  subtitle: p.string.isRequired,
  /** Text to be displayed on the button. */
  buttonText: p.string.isRequired,
  /** The href value to navigate to when the user will click the button. */
  buttonHref: p.string.isRequired,
  /** The URL of the logo image to display above the title. If not provided then no logo image will be rendered. */
  logoImgUrl: p.string,
  /** The value of the background-position rule of the container (default: center). */
  bgImgPosition: p.string,
  /** Set this to true if you want the button to use a vanilla `a` tag instead of the router `Link`. You need this if you want to navigate to an external resources. */
  useVanillaLink: p.bool,
};

LinkBanner.defaultProps = {
  useVanillaLink: false,
};

export default LinkBanner;

export const CustomLinkBanner = (props) => {
  const { bgColor, bgImgUrl, bgSpectrumColor, bgImgPosition, alignItems, children, ...rest } = props;
  return (
    <BannerContainer
      $imgUrl={bgImgUrl}
      $bgColor={bgColor}
      $spectrumColor={bgSpectrumColor}
      $backgroundPosition={bgImgPosition}
      $otherStyles={rest}
    >
      {bgImgUrl && (
        <BackgroundImgContainer>
          <object
            type="image/webp"
            style={{ height: '100%', width: '100%', objectFit: 'cover' }}
            data={getNextGenImageUrl(bgImgUrl)}
          >
            <img src={bgImgUrl} alt="" style={{ height: '100%', minWidth: '100%' }} />
          </object>
        </BackgroundImgContainer>
      )}
      <BannerContentContainer $bgColor={bgImgUrl ? 'rgba(0,0,0,0.3)' : 'none'} $alignItems={alignItems}>
        {children}
      </BannerContentContainer>
    </BannerContainer>
  );
};

CustomLinkBanner.Title = Title;
CustomLinkBanner.Subtitle = Subtitle;

CustomLinkBanner.propTypes = {
  /** The URL of the image that will be used in the background. If not set then `bgSpectrumColor` will be used. */
  bgImgUrl: p.string,
  /** Spectrum global color string for the background (eg: red-500). If not set then `bgColor` will be used. */
  bgSpectrumColor: p.string,
  /** CSS global color string for the background (eg: blue). If not set then black will be used. */
  bgColor: p.string,
  /** The value of the background-position rule of the container (default: center). */
  bgImgPosition: p.string,
  /** The value of the align-items rule of the content container (default: flex-start). */
  alignItems: p.oneOf(['flex-start', 'center', 'flex-end']),
  children: p.oneOfType([p.arrayOf(p.node), p.node]).isRequired,
};
